<template>
    <div class="crc-card" :class="[tierClass, { 'crc-expired': isOverdue }]" draggable="true">
        <div class="crc-card-top">
            <span class="crc-category" :style="categoryStyle">{{ categoryName }}</span>
            <span class="crc-expired-badge" v-if="isOverdue"><i class="fas fa-exclamation-circle"></i> Overdue</span>
            <span class="crc-req-number">{{ request.requestNumber }}</span>
        </div>

        <div class="crc-title">{{ request.title }}</div>

        <div class="crc-amount">
            <span class="crc-rupee">₹</span>{{ formattedAmount }}
        </div>

        <div class="crc-meta">
            <div class="crc-meta-item" v-if="request.requestedBy">
                <i class="fas fa-user"></i>
                <span>{{ request.requestedBy.fullName || request.requestedBy }}</span>
            </div>
            <div class="crc-meta-item" v-if="departmentName">
                <i class="fas fa-building"></i>
                <span>{{ departmentName }}</span>
            </div>
        </div>

        <div class="crc-footer">
            <span class="crc-date" :class="{ 'crc-date-expired': isOverdue }">
                <i class="fas fa-calendar-alt"></i> {{ formatDate(request.billDueDate || request.createdAt ||
                    request.created_at) }}
            </span>
            <span class="crc-priority" :style="priorityStyle" v-if="priorityName">
                {{ priorityName }}
            </span>
        </div>

        <!-- Tier badge -->
        <div class="crc-tier-indicator" v-if="tierLabel">{{ tierLabel }}</div>

        <!-- Drag indicator -->
        <div class="crc-drag-indicator">
            <i class="fas fa-grip-vertical"></i>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        request: { type: Object, required: true },
    },
    computed: {
        categoryName() {
            if (this.request.category && typeof this.request.category === "object") return this.request.category.name
            return this.request.category || "Other"
        },
        categoryStyle() {
            // Use a neutral pill style; backend doesn't provide per-category colors
            return { background: "#f1f5f9", color: "#475569" }
        },
        priorityName() {
            if (this.request.priority && typeof this.request.priority === "object") return this.request.priority.name
            return this.request.priority || ""
        },
        priorityStyle() {
            const p = this.request.priority
            if (p && typeof p === "object" && p.color) {
                return { background: p.color + "20", color: p.color }
            }
            return {}
        },
        departmentName() {
            if (this.request.department && typeof this.request.department === "object") return this.request.department.name
            return this.request.department || ""
        },
        tierLabel() {
            if (this.request.tier && typeof this.request.tier === "object") return this.request.tier.name
            return this.request.tier ? `T${this.request.tier}` : ""
        },
        tierClass() {
            const t = this.request.tier
            if (t && typeof t === "object") return "crc-tier-accent"
            return t ? "crc-tier-" + t : ""
        },
        formattedAmount() {
            if (!this.request.amount) return "0"
            return Number(this.request.amount).toLocaleString("en-IN")
        },
        isOverdue() {
            const status = this.request.status
            const statusCode = (status && typeof status === "object") ? status.code : status
            if (statusCode === "settled") return false
            if (this.request.isOverdue) return true
            if (!this.request.billDueDate) return false
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            return new Date(this.request.billDueDate) < now
        },
    },
    methods: {
        formatDate(d) {
            if (!d) return ""
            const dt = new Date(d)
            return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.crc-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 12px;
    cursor: grab;
    position: relative;
    transition: box-shadow $transitionFast, border-color $transitionFast, transform $transitionFast;

    &:hover {
        box-shadow: $shadowMd;
        border-color: $gray200;
        transform: translateY(-1px);
    }

    &:active {
        cursor: grabbing;
        opacity: 0.85;
    }
}

/* Tier left border accent */
.crc-tier-accent {
    border-left: 3px solid $primaryColor;
}

.crc-tier-1 {
    border-left: 3px solid #94a3b8;
}

.crc-tier-2 {
    border-left: 3px solid #3b82f6;
}

.crc-tier-3 {
    border-left: 3px solid #f59e0b;
}

.crc-tier-4 {
    border-left: 3px solid #ef4444;
}

.crc-tier-5 {
    border-left: 3px solid #7c3aed;
}

/* Expired / Overdue state */
.crc-expired {
    background: #fef2f2;
    border-color: #fecaca;
}

.crc-expired:hover {
    border-color: #f87171;
}

.crc-expired-badge {
    font-size: 10px;
    font-weight: 700;
    color: #dc2626;
    display: flex;
    align-items: center;
    gap: 3px;
}

.crc-date-expired {
    color: #dc2626;
    font-weight: 600;
}

/* Top Row */
.crc-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    gap: 4px;
}

.crc-category {
    font-size: 10.5px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: $radiusFull;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.crc-req-number {
    font-size: 10px;
    font-weight: 600;
    color: $gray400;
    flex-shrink: 0;
}

.crc-tier-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 9px;
    font-weight: 800;
    padding: 2px 6px;
    border-radius: 4px;
    background: $gray100;
    color: $gray600;
}

/* Title */
.crc-title {
    font-size: 13px;
    font-weight: 600;
    color: $gray800;
    line-height: 1.35;
    margin-bottom: 6px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Amount */
.crc-amount {
    font-size: 17px;
    font-weight: 800;
    color: $gray900;
    margin-bottom: 8px;
}

.crc-rupee {
    font-size: 14px;
    font-weight: 600;
    color: $gray500;
    margin-right: 1px;
}

/* Meta */
.crc-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-bottom: 8px;
}

.crc-meta-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    color: $gray500;

    i {
        font-size: 10px;
        color: $gray300;
        width: 12px;
        text-align: center;
    }
}

/* Footer */
.crc-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.crc-date {
    font-size: 10.5px;
    color: $gray400;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
        font-size: 10px;
    }
}

.crc-priority {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: $radiusFull;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

/* Drag grip icon */
.crc-drag-indicator {
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    opacity: 0;
    color: $gray300;
    font-size: 11px;
    transition: opacity $transitionFast;
}

.crc-card:hover .crc-drag-indicator {
    opacity: 1;
}
</style>
